import { PositionSide } from '@kwenta/sdk/types'
import { formatDollars } from '@kwenta/sdk/utils'
import ROUTES from 'constants/routes'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import Badge from 'components/Badge'
import ColoredPrice from 'components/ColoredPrice'
import Currency from 'components/Currency'
import Loader from 'components/Loader'
import { DesktopOnlyView, MobileOrTabletView } from 'components/Media'
import Pill from 'components/Pill'
import Table, { TableHeader, TableNoResults } from 'components/Table'
import { EmptyTableContainer } from 'components/Table/Table'
import { Body } from 'components/Text'
import { CustomFontLabel } from 'components/Text/CustomFontLabel'
import { FlexDiv, FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { NO_VALUE } from 'constants/placeholder'
import {
	selectMarketAsset,
	selectPerpsProvider,
	selectUserInfoShowAllMarkets,
} from 'state/futures/common/selectors'
import { selectCancellingConditionalOrder } from 'state/futures/isolatedMargin/selectors'
import { selectConditionalOrderTableItems } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectIsWatcherMode } from 'state/wallet/selectors'

import PositionType from '../PositionType'

import { TransactionPencilButton } from 'components/Button/PencilButton'
import {
	filterConditionalOrders,
	markCancellableConditionalOrders,
	processConditionalOrders,
	processGroupedConditionalOrders,
} from 'utils/futures'
import TableMarketDetails from './TableMarketDetails'

export default function ConditionalOrdersTable({
	primaryBackground = true,
}: { primaryBackground?: boolean }) {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const router = useRouter()

	const perpsProvider = useAppSelector(selectPerpsProvider)
	const marketAsset = useAppSelector(selectMarketAsset)
	const isCancellingOrder = useAppSelector(selectCancellingConditionalOrder)
	const isWatcherMode = useAppSelector(selectIsWatcherMode)
	const userInfoShowAllMarkets = useAppSelector(selectUserInfoShowAllMarkets)
	const orders = useAppSelector(selectConditionalOrderTableItems)

	const isMarketsPage = useMemo(() => router.pathname.includes('/market'), [router.pathname])

	const rows = useMemo(() => {
		const processedOrders = processConditionalOrders(orders, dispatch)
		const filteredOrders = filterConditionalOrders(
			processedOrders,
			marketAsset,
			userInfoShowAllMarkets,
			isMarketsPage
		)
		const processedRows = processGroupedConditionalOrders(filteredOrders, perpsProvider)
		return markCancellableConditionalOrders(processedRows, isCancellingOrder)
	}, [
		orders,
		dispatch,
		isCancellingOrder,
		marketAsset,
		userInfoShowAllMarkets,
		isMarketsPage,
		perpsProvider,
	])

	return (
		<>
			<DesktopOnlyView>
				<StyledTable
					data={rows}
					highlightRowsOnHover
					noBottom
					// @ts-ignore
					$primaryBackground={primaryBackground}
					rounded={false}
					noResultsMessage={
						<TableNoResults>{t('futures.market.user.open-orders.table.no-result')}</TableNoResults>
					}
					columns={[
						{
							header: () => (
								<TableHeader>{t('futures.market.user.open-orders.table.market-type')}</TableHeader>
							),
							accessorKey: 'market',
							cell: (cellProps) => {
								return (
									<MarketDetailsContainer
										onClick={() =>
											router.push(
												ROUTES.Markets.MarketPair(cellProps.row.original.asset, perpsProvider)
											)
										}
									>
										<TableMarketDetails
											marketName={cellProps.row.original.marketName}
											infoLabel={cellProps.row.original.orderTypeDisplay}
											assetKey={cellProps.row.original.asset}
											badge={
												cellProps.row.original.isStale ? (
													<ExpiredBadge>
														{t('futures.market.user.open-orders.badges.expired')}
													</ExpiredBadge>
												) : undefined
											}
											perpsProvider={cellProps.row.original.provider}
										/>
									</MarketDetailsContainer>
								)
							},
							enableSorting: true,
							size: 70,
						},
						{
							header: () => (
								<TableHeader>{t('futures.market.user.open-orders.table.side')}</TableHeader>
							),
							accessorKey: 'side',
							cell: (cellProps) => {
								return <PositionType side={cellProps.row.original.tradeDirection} />
							},
							enableSorting: true,
							size: 40,
						},
						{
							header: () => (
								<TableHeader>{t('futures.market.user.open-orders.table.type')}</TableHeader>
							),
							accessorKey: 'type',
							cell: (cellProps) => {
								return <Body>{cellProps.row.original.orderTypeDisplay}</Body>
							},
							enableSorting: true,
							size: 50,
						},
						{
							header: () => (
								<TableHeader>{t('futures.market.user.open-orders.table.reduce-only')}</TableHeader>
							),
							accessorKey: 'reduceOnly',
							cell: (cellProps) => {
								return <Body>{cellProps.row.original.reduceOnly ? 'Yes' : 'No'}</Body>
							},
							enableSorting: true,
							size: 50,
						},
						{
							header: () => (
								<TableHeader>{t('futures.market.user.open-orders.table.size')}</TableHeader>
							),
							accessorKey: 'size',
							cell: (cellProps) => {
								const cancellingRow = cellProps.row.original.isCancelling
								return (
									<FlexDivRowCentered justifyContent="flex-start" columnGap="5px">
										<CustomFontLabel text={cellProps.row.original.sizeTxt} />
										{!cellProps.row.original.isSlTp && (
											<TransactionPencilButton
												width={12}
												height={12}
												onClick={() => cellProps.row.original.edit('size')}
												disabled={cancellingRow || isWatcherMode}
											/>
										)}
									</FlexDivRowCentered>
								)
							},
							enableSorting: true,
							size: 50,
						},
						{
							header: () => (
								<TableHeader>
									{t('futures.market.user.open-orders.table.current-price')}
								</TableHeader>
							),
							accessorKey: 'price',
							cell: (cellProps) => {
								return cellProps.row.original.currentPrice ? (
									<div>
										<ColoredPrice priceChange={cellProps.row.original.currentPrice.change}>
											{formatDollars(cellProps.row.original.currentPrice.price, {
												suggestDecimals: true,
											})}
										</ColoredPrice>
									</div>
								) : (
									NO_VALUE
								)
							},
							enableSorting: true,
							size: 50,
						},
						{
							header: () => (
								<TableHeader>{t('futures.market.user.open-orders.table.price')}</TableHeader>
							),
							accessorKey: 'targetPrice',
							cell: (cellProps) => {
								const cancellingRow = cellProps.row.original.isCancelling
								return (
									<FlexDivRowCentered justifyContent="flex-start" columnGap="5px">
										<Currency.Price
											price={cellProps.row.original.targetPrice}
											formatOptions={{ suggestDecimals: true }}
										/>
										{!cellProps.row.original.isSlTp && (
											<TransactionPencilButton
												width={12}
												height={12}
												disabled={cancellingRow || isWatcherMode}
												onClick={() => cellProps.row.original.edit('targetPrice')}
											/>
										)}
									</FlexDivRowCentered>
								)
							},
							enableSorting: true,
							size: 50,
						},
						{
							header: () => (
								<TableHeader>
									{t('futures.market.user.open-orders.table.reserved-margin')}
								</TableHeader>
							),
							accessorKey: 'marginDelta',
							cell: (cellProps) => {
								const { marginDelta } = cellProps.row.original
								return <div>{formatDollars(marginDelta?.gt(0) ? marginDelta : '0')}</div>
							},
							enableSorting: true,
							size: 50,
						},
						{
							header: () => (
								<TableHeader>{t('futures.market.user.open-orders.table.tp-sl')}</TableHeader>
							),
							accessorKey: 'tpSl',
							cell: (cellProps) => {
								const cancellingRow = cellProps.row.original.isCancelling
								return (
									<FlexDivCol>
										<FlexDivRowCentered columnGap="5px">
											{!cellProps.row.original.takeProfitPrice ? (
												<Body>{NO_VALUE}</Body>
											) : (
												<Currency.Price
													price={cellProps.row.original.takeProfitPrice}
													formatOptions={{ suggestDecimals: true }}
												/>
											)}
											{(cellProps.row.original.stopLossPrice ||
												cellProps.row.original.takeProfitPrice) && (
												<TransactionPencilButton
													width={12}
													height={12}
													disabled={cancellingRow || isWatcherMode}
													onClick={() => {
														cellProps.row.original.edit('tpSl')
													}}
												/>
											)}
										</FlexDivRowCentered>
										{!cellProps.row.original.stopLossPrice ? (
											<Body>{NO_VALUE}</Body>
										) : (
											<Currency.Price
												price={cellProps.row.original.stopLossPrice}
												formatOptions={{ suggestDecimals: true }}
											/>
										)}
									</FlexDivCol>
								)
							},
							enableSorting: true,
							size: 50,
						},
						{
							header: () => (
								<TableHeader>{t('futures.market.user.open-orders.table.actions')}</TableHeader>
							),
							accessorKey: 'actions',
							cell: (cellProps) => {
								const cancellingRow = cellProps.row.original.isCancelling
								return (
									<FlexDivRowCentered columnGap="4px">
										<Pill
											color="red"
											size="medium"
											onClick={cellProps.row.original.cancel}
											disabled={cancellingRow || isWatcherMode}
										>
											{cancellingRow ? (
												<Loader inline height="11px" />
											) : (
												t('futures.market.user.open-orders.actions.cancel')
											)}
										</Pill>
									</FlexDivRowCentered>
								)
							},
							size: 50,
						},
					]}
				/>
			</DesktopOnlyView>
			<MobileOrTabletView>
				{rows.length === 0 ? (
					<EmptyTableContainer>
						<TableNoResults>{t('futures.market.user.open-orders.table.no-result')}</TableNoResults>
					</EmptyTableContainer>
				) : (
					rows.map((order) => {
						return (
							<OrderItem key={order.id}>
								<OrderMeta $side={order.tradeDirection!}>
									<FlexDiv>
										<div className="position-side-bar" />
										<div>
											<Body>{order.marketName}</Body>
											<Body capitalized color="secondary">
												{order.orderTypeDisplay}
											</Body>
										</div>
									</FlexDiv>
									<FlexDiv>
										<Pill size="medium" color="red" onClick={order.cancel}>
											{t('futures.market.user.open-orders.actions.cancel')}
										</Pill>
									</FlexDiv>
								</OrderMeta>
								<OrderRow>
									<Body color="secondary">{t('futures.market.user.open-orders.table.side')}</Body>
									<PositionType side={order.tradeDirection!} />
								</OrderRow>
								<OrderRow>
									<Body color="secondary">
										{t('futures.market.user.open-orders.table.reduce-only')}
									</Body>
									<Body>{order.reduceOnly ? 'Yes' : 'No'}</Body>
								</OrderRow>
								<OrderRow>
									<Body color="secondary">{t('futures.market.user.open-orders.table.size')}</Body>
									<FlexDivRowCentered columnGap="5px">
										<Body>{order.sizeTxt}</Body>
										{!order.isSlTp && (
											<TransactionPencilButton
												width={12}
												height={12}
												onClick={() => order.edit('size')}
											/>
										)}
									</FlexDivRowCentered>
								</OrderRow>
								<OrderRow>
									<Body color="secondary">
										{t('futures.market.user.open-orders.table.current-price')}
									</Body>
									{order.currentPrice?.price ? (
										<ColoredPrice priceChange={order.currentPrice.change}>
											{formatDollars(order.currentPrice.price, { suggestDecimals: true })}
										</ColoredPrice>
									) : (
										NO_VALUE
									)}
								</OrderRow>
								<OrderRow>
									<Body color="secondary">{t('futures.market.user.open-orders.table.price')}</Body>
									<FlexDivRowCentered columnGap="5px">
										<Currency.Price price={order.targetPrice} />
										{!order.isSlTp && (
											<TransactionPencilButton
												width={12}
												height={12}
												onClick={() => order.edit('targetPrice')}
											/>
										)}
									</FlexDivRowCentered>
								</OrderRow>
								<OrderRow>
									<Body color="secondary">
										{t('futures.market.user.open-orders.table.reserved-margin')}
									</Body>
									<Body mono>
										{formatDollars(order.marginDelta?.gt(0) ? order.marginDelta : '0')}
									</Body>
								</OrderRow>
								<OrderRow>
									<Body color="secondary">{t('futures.market.user.open-orders.table.tp-sl')}</Body>
									<FlexDivRowCentered columnGap="5px">
										<FlexDivCol>
											{!order.takeProfitPrice ? (
												<Body>{NO_VALUE}</Body>
											) : (
												<Currency.Price
													price={order.takeProfitPrice}
													formatOptions={{ suggestDecimals: true }}
												/>
											)}
											{!order.stopLossPrice ? (
												<Body>{NO_VALUE}</Body>
											) : (
												<Currency.Price
													price={order.stopLossPrice}
													formatOptions={{ suggestDecimals: true }}
												/>
											)}
										</FlexDivCol>
										{(order.takeProfitPrice || order.stopLossPrice) && (
											<TransactionPencilButton
												width={12}
												height={12}
												onClick={() => {
													order.edit('tpSl')
												}}
											/>
										)}
									</FlexDivRowCentered>
								</OrderRow>
							</OrderItem>
						)
					})
				)}
			</MobileOrTabletView>
		</>
	)
}

const ExpiredBadge = styled(Badge)`
	background: ${(props) => props.theme.colors.selectedTheme.red};
	padding: 1px 5px;
	line-height: 9px;
	margin-left: 5px;
`

const OrderMeta = styled.div<{ $side: PositionSide }>`
	display: flex;
	justify-content: space-between;
	margin-bottom: 20px;

	.position-side-bar {
		height: 100%;
		width: 4px;
		margin-right: 8px;
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.text[
				props.$side === PositionSide.LONG ? 'positive' : 'negative'
			]};
	}
`

const StyledTable = styled(Table)<{ $primaryBackground?: boolean }>`
	.table-body-row {
		border-bottom: none;
	}
	background-color: ${(props) =>
		props.$primaryBackground
			? props.theme.colors.selectedTheme.newTheme.containers.primary.background
			: 'none'};
` as typeof Table

const OrderItem = styled.div`
	margin: 0 20px;
	padding: 20px 0;

	&:not(:last-of-type) {
		border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
	}
`

const OrderRow = styled.div`
	display: flex;
	justify-content: space-between;

	&:not(:last-of-type) {
		margin-bottom: 10px;
	}
`

const MarketDetailsContainer = styled.div`
	display: flex;
	height: 100%;
	flex-direction: row;
	cursor: pointer;
`
